import { useCallback, useState } from 'react'

import type { UseAgentCreateFormResult } from 'features/trend-detection/features/agent-create/hooks/use-agent-create-form'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type {
  CreateAgentJobResponse,
  IssueAgentResponse,
} from 'features/trend-detection/types'
import { buildAgentFiltersV2 } from 'features/trend-detection/utils/agent-filters-v2'

export interface UseAgentCreateSubmitParams {
  service: TrendDetectionService
  projectId: string | null
  form: UseAgentCreateFormResult
}

export interface CreateAndRunResult {
  agent: IssueAgentResponse
  runResponse: CreateAgentJobResponse
}

export interface UseAgentCreateSubmitResult {
  createAndRun: () => Promise<CreateAndRunResult>
  isCreating: boolean
}

/** Issue Agent's create-and-run. Absorbs the deleted `use-create-agent.ts` +
 * the POST body `use-agent-setup.ts`'s `createAgent` used to build, now
 * sourced from `use-agent-create-form`'s state. Benchmarking and Reports
 * agents keep their own create paths (`useBenchmarkingSetup.saveAgent` /
 * `insightAgentService.createAgent`, called directly from Container) — they
 * always lived outside a dedicated hook, and unifying three genuinely
 * different request shapes into one hook would trade real clarity for a
 * DRY-looking wrapper. See the concept matrix in the create-agent-flow plan. */
export function useAgentCreateSubmit({
  service,
  projectId,
  form,
}: UseAgentCreateSubmitParams): UseAgentCreateSubmitResult {
  const [isCreating, setIsCreating] = useState(false)

  const createAndRun = useCallback(async (): Promise<CreateAndRunResult> => {
    if (!projectId) throw new Error('No project selected')
    if (!form.agentName.trim()) throw new Error('Please enter an agent name')

    setIsCreating(true)
    try {
      // Returns the criteria to submit with directly — form.criteria may not
      // have re-rendered yet when a suggest-on-submit run resolves.
      const criteria = await form.ensureFiltersBeforeSubmit()
      const agent = await service.createAgent(projectId, {
        name: form.agentName.trim(),
        description: form.prompt.trim() || undefined,
        agent_type: 'issue_detection',
        config: {
          prompt: form.prompt.trim() || form.agentName.trim(),
          filters_v2: buildAgentFiltersV2(criteria),
        },
        sensitivity: form.sensitivity,
      })
      if (form.emailNotifications) {
        await service.updateEmailPreference(projectId, agent.uuid, true)
      }
      const runResponse = await service.runAgent(projectId, agent.uuid, {})
      return { agent, runResponse }
    } finally {
      setIsCreating(false)
    }
  }, [projectId, form, service])

  return { createAndRun, isCreating }
}
